import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { InsightBar } from '../../components/charts/InsightBar.jsx';
import { useChartPalette } from '../../components/charts/useChartPalette.js';
import { EmptyState } from '../../components/ui/EmptyState.jsx';
import { Skeleton } from '../../components/ui/Skeleton.jsx';
import { CsvExportButton } from '../../components/data/CsvExportButton.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { projectsApi } from '../../services/projectsApi.js';
import { formatCurrency } from '../../utils/formatters.js';
import { countBy, sumBy } from '../../utils/insightStats.js';
import { ProjectCreateModal } from './ProjectCreateModal.jsx';

const CSV_COLUMNS = [
  { key: 'code', label: 'Code' },
  { key: 'name', label: 'Name' },
  { key: 'customerName', label: 'Customer' },
  { key: 'status', label: 'Status' },
  { key: 'budgetAmount', label: 'Budget' },
  { key: 'actualAmount', label: 'Actual' },
  { key: 'currency', label: 'Currency' },
];

const STATUS_ORDER = ['ACTIVE', 'PLANNED', 'PAUSED', 'COMPLETED', 'CANCELLED'];

function projectStatusColor(palette, status) {
  if (status === 'PLANNED') return palette?.series?.[4];
  if (status === 'ACTIVE') return palette?.series?.[1];
  if (status === 'PAUSED') return palette?.series?.[2];
  if (status === 'COMPLETED') return palette?.series?.[0];
  return palette?.axis;
}

function budgetPct(project) {
  const budget = Number(project.budgetAmount) || 0;
  const actual = Number(project.actualAmount) || 0;
  if (budget <= 0) {
    return 0;
  }
  return Math.min(100, Math.round((actual / budget) * 100));
}

export function ProjectsListPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);

  const projects = useQuery({ queryKey: ['projects', 'list'], queryFn: () => projectsApi.listProjects({ limit: 200 }).then((r) => r.items) });

  const palette = useChartPalette();
  const stats = useMemo(() => {
    const rows = projects.data || [];
    const counts = countBy(rows, (r) => r.status);
    const statuses = [
      ...STATUS_ORDER.filter((s) => counts[s]),
      ...Object.keys(counts).filter((s) => !STATUS_ORDER.includes(s)),
    ];
    return {
      count: rows.length,
      statuses,
      counts,
      active: counts.ACTIVE || 0,
      budget: sumBy(rows, (r) => r.budgetAmount),
      spent: sumBy(rows, (r) => r.actualAmount),
    };
  }, [projects.data]);

  const createMutation = useMutation({
    mutationFn: (body) => projectsApi.createProject(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', 'list'] });
      setModalOpen(false);
    },
  });

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('projects.list.title')}</h1>
          <p className="muted">{t('projects.list.subtitle')}</p>
        </div>
        <div className="page-actions">
          <CsvExportButton rows={projects.data || []} columns={CSV_COLUMNS} filename="projekte" />
          <button type="button" className="btn primary" onClick={() => setModalOpen(true)}>
            {t('projects.list.new')}
          </button>
        </div>
      </header>

      {stats.count > 0 && (
        <InsightBar
          title={t('insight.byStatus')}
          labels={stats.statuses.map((s) => (s === '-' ? s : t(`projects.status.${s}`)))}
          values={stats.statuses.map((s) => stats.counts[s])}
          colors={stats.statuses.map((s) => projectStatusColor(palette, s))}
          metrics={[
            { label: t('insight.count'), value: stats.count },
            { label: t('insight.active'), value: stats.active },
            { label: t('projects.insight.budget'), value: formatCurrency(stats.budget) },
            { label: t('projects.insight.spent'), value: formatCurrency(stats.spent) },
          ]}
        />
      )}

      {projects.isLoading ? <Skeleton variant="cards" count={6} /> : (projects.data || []).length === 0 ? (
        <EmptyState message={t('projects.list.empty')} />
      ) : (
        <div className="project-grid">
          {projects.data.map((p) => (
            <Link key={p.id} to={`/projects/${p.id}`} className="project-card">
              <div className="project-card-top">
                <span className="project-card-code">{p.code}</span>
                <span className={`status-pill status-${p.status.toLowerCase()}`}>{t(`projects.status.${p.status}`)}</span>
              </div>
              <div className="project-card-name">{p.name}</div>
              <div className="project-card-meta">{p.customerName || t('common.none')}</div>
              <div className="project-card-budget">
                <div className="project-card-budget-bar">
                  <div className="project-card-budget-fill" style={{ width: `${budgetPct(p)}%` }} />
                </div>
                <div className="project-card-budget-text">
                  {formatCurrency(p.actualAmount)} / {formatCurrency(p.budgetAmount)}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      <ProjectCreateModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreate={(body) => createMutation.mutate(body)}
      />
    </section>
  );
}
